import { useEffect, useState } from 'react';

import styled from 'styled-components';
import { toast } from 'react-toastify';
import { BiArrowFromTop, BiArrowToTop, BiTargetLock } from 'react-icons/bi';
import { FcNeutralTrading } from 'react-icons/fc';
import { GrRevert } from 'react-icons/gr';

import {
    GetMicrostoreColumnQuery,
    useGetColumnProductsStockInfoQuery,
    useGetNextMsDeliveryDateQuery,
    useUpdateMicrostoreColumnMutation,
    TotadminPermission,
} from 'data/__generated__';

import { SectionContainer } from 'components/DetailsView/Section/SectionContainer';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { ColumnLocationSelector } from './components/ColumnLocationSelector';
import { LocationEditor } from 'pages/MicrostoreColumnGroups/MicrostoreColumnGroupDetails/LocationEditor';
import { ExpiryDateEditor } from 'pages/MicrostoreColumnGroups/MicrostoreColumnGroupDetails/ExpiryDateEditor';
import { QuantityGenerationPopup } from 'pages/MicrostoreColumnGroups/Popups/QuantityGenerationPopup';
import { StockTransferGenerationPopup } from 'pages/MicrostoreColumnGroups/Popups/StockTransferGenerationPopup';
import {
    MicrostoreColumnFormValues,
    MicrostoreLocationFormValues,
    MicrostoreProductsStockInfoFormValues,
    MicrostoreShelfFormValues,
    mapMicrostoreColumnFormValuesToMicrostoreColumnUpdateInput,
} from 'pages/MicrostoreColumnGroups/MicrostoreColumnGroupDetails/FormHelper/DataMapper';

export const EmplacementsSection = ({
    microstoreColumn,
}: {
    microstoreColumn: GetMicrostoreColumnQuery['microstoreColumn'];
}) => {
    const microstoreColumnId = microstoreColumn._id;

    const [columnFormValues, setColumnFormValues] = useState<MicrostoreColumnFormValues>(
        microstoreColumn as unknown as MicrostoreColumnFormValues,
    );
    const [isFormDirty, setIsFormDirty] = useState(false);
    const [revertUpdateStack, setRevertUpdateStack] = useState<(() => void)[]>([]);

    useEffect(() => {
        if (isFormDirty) {
            return;
        }
        setColumnFormValues(microstoreColumn as unknown as MicrostoreColumnFormValues);
    }, [microstoreColumn, isFormDirty]);

    const [isToday, setIsToday] = useState(true);
    const [selectedShelfIndex, setSelectedShelfIndex] = useState<number | null>(null);
    const [selectedLocationPosition, setSelectedLocationPosition] = useState<{
        column: number;
        row: number;
    } | null>(null);
    const [isQuantityGenerationPopupOpen, setIsQuantityGenerationPopupOpen] = useState<boolean>(false);
    const [isStockTransferPopupOpen, setIsStockTransferPopupOpen] = useState<boolean>(false);

    const { data: nextMSDeliveryDateData } = useGetNextMsDeliveryDateQuery();
    const nextMSDeliveryDate = nextMSDeliveryDateData?.getNextMSDeliveryDate ?? null;

    const { loading: stockInfoLoading, data: stockInfoData } = useGetColumnProductsStockInfoQuery({
        variables: { columnId: microstoreColumnId },
        skip: !microstoreColumnId,
    });

    const [updateMicrostoreColumn, { loading: updateLoading }] = useUpdateMicrostoreColumnMutation();

    const isSidebarOpen = selectedShelfIndex !== null && selectedLocationPosition !== null;

    const selectedShelf: MicrostoreShelfFormValues | null =
        selectedShelfIndex !== null ? (columnFormValues.shelves[selectedShelfIndex] ?? null) : null;

    const selectedLocation: MicrostoreLocationFormValues | null =
        selectedShelf !== null && selectedLocationPosition !== null
            ? (selectedShelf.locationTable[selectedLocationPosition.row]?.[selectedLocationPosition.column] ?? null)
            : null;

    const allSiteLocations = columnFormValues.shelves.flatMap((shelf) => shelf.locationTable.flatMap((row) => row));

    const columnProducts = stockInfoData?.column?.products ?? null;

    // Flatten product + microstoreStockInfo into the flat format expected by child components
    const microstoreProductsStockInfo = columnProducts
        ? columnProducts
              .filter((p) => p.microstoreStockInfo != null)
              .map(({ microstoreStockInfo, __typename: _, ...product }) => ({
                  ...product,
                  ...microstoreStockInfo!,
              }))
        : null;

    // LocationEditor and ExpiryDateEditor (MSGroup components) need stockUsedInMicrostore.
    // We compute it here only for the sidebar, not in the main display chain.
    const sidebarStockInfo: MicrostoreProductsStockInfoFormValues | null = microstoreProductsStockInfo
        ? microstoreProductsStockInfo.map((p) => ({
              ...p,
              stockUsedInMicrostore: allSiteLocations
                  .filter((loc) => loc.replacementProductId === p._id)
                  .reduce((sum, loc) => sum + loc.stockToAdd, 0),
              isPresentTomorrow: allSiteLocations.some((loc) => loc.replacementProductId === p._id),
          }))
        : null;

    function updateColumn(values: Partial<MicrostoreColumnFormValues>) {
        const prevColumn = columnFormValues;
        const newColumn = { ...columnFormValues, ...values };
        setColumnFormValues(newColumn);
        setIsFormDirty(true);
        setRevertUpdateStack((prev) => [
            () => {
                setColumnFormValues(prevColumn);
                setIsFormDirty(true);
            },
            ...prev,
        ]);
    }

    function updateLocationAt({
        shelfIndex,
        row,
        column,
        values,
    }: {
        shelfIndex: number;
        row: number;
        column: number;
        values: Partial<MicrostoreLocationFormValues>;
    }) {
        const targetShelf = columnFormValues.shelves[shelfIndex];
        if (!targetShelf) {
            return;
        }
        const targetRow = targetShelf.locationTable[row];
        const targetLocation = targetRow?.[column];
        if (!targetLocation) {
            return;
        }
        const newLocation = { ...targetLocation, ...values };
        const newLocationRow = [...targetRow.slice(0, column), newLocation, ...targetRow.slice(column + 1)];
        const newShelf: MicrostoreShelfFormValues = {
            ...targetShelf,
            locationTable: [
                ...targetShelf.locationTable.slice(0, row),
                newLocationRow,
                ...targetShelf.locationTable.slice(row + 1),
            ],
        };
        const newShelves = [
            ...columnFormValues.shelves.slice(0, shelfIndex),
            newShelf,
            ...columnFormValues.shelves.slice(shelfIndex + 1),
        ];
        updateColumn({ shelves: newShelves });
    }

    function updateSelectedLocation(values: Partial<MicrostoreLocationFormValues>) {
        if (selectedShelfIndex === null || selectedLocationPosition === null) {
            return;
        }
        updateLocationAt({
            shelfIndex: selectedShelfIndex,
            row: selectedLocationPosition.row,
            column: selectedLocationPosition.column,
            values,
        });
    }

    function revertLastUpdate() {
        const [revertUpdate, ...rest] = revertUpdateStack;
        if (revertUpdate) {
            revertUpdate();
        }
        setRevertUpdateStack(rest);
    }

    function fillJ1FromJ() {
        const newColumn = {
            ...columnFormValues,
            shelves: columnFormValues.shelves.map((shelf) => ({
                ...shelf,
                locationTable: shelf.locationTable.map((row) =>
                    row.map((location) => ({
                        ...location,
                        replacementProductId:
                            location.replacementProductId === null ? location.productId : location.replacementProductId,
                        stockToAdd: location.replacementProductId === null ? 0 : location.stockToAdd,
                    })),
                ),
            })),
        };
        updateColumn(newColumn);
    }

    function fillJFromTemplate() {
        const newColumn = {
            ...columnFormValues,
            shelves: columnFormValues.shelves.map((shelf) => ({
                ...shelf,
                locationTable: shelf.locationTable.map((row) =>
                    row.map((location) => {
                        const templateProduct = location.microstoreLocationTemplate.product;
                        if (!templateProduct) {
                            return location;
                        }
                        return { ...location, productId: templateProduct._id };
                    }),
                ),
            })),
        };
        updateColumn(newColumn);
    }

    async function submitUpdate() {
        const microstoreColumnUpdateInput = mapMicrostoreColumnFormValuesToMicrostoreColumnUpdateInput({
            microstoreColumnFormValues: columnFormValues,
        });
        const { data } = await updateMicrostoreColumn({
            variables: { microstoreColumnUpdateInput },
        });

        if (!data) {
            throw new Error('Une erreur est survenue lors de la modification de la colonne');
        }

        const {
            updateMicrostoreColumnMutation: { newMicrostoreColumn, errors },
        } = data;

        if (errors.length) {
            errors.forEach((error, index) => {
                toast.error(<span key={index}>Erreur : {error}</span>, { autoClose: false });
            });
        }

        if (errors.length) {
            toast.info('Cliquez pour fermer toutes les notifications', {
                autoClose: false,
                onClick: () => toast.dismiss(),
            });
        }

        if (newMicrostoreColumn) {
            toast.success('La colonne a bien été modifiée !');
            setIsFormDirty(false);
            setRevertUpdateStack([]);
        }
    }

    if (!microstoreColumnId) {
        return (
            <SectionContainer title="Emplacements" isInitiallyOpen>
                <EmptyMessage>Cette colonne n'est pas attachée à un site.</EmptyMessage>
            </SectionContainer>
        );
    }

    const actionGroups: PageActionGroup[] = [
        {
            items: [
                {
                    label: 'Revert dernière modification',
                    icon: <GrRevert size={14} />,
                    onClick: revertLastUpdate,
                    disabled: !isFormDirty,
                },
            ],
        },
        {
            items: [
                {
                    label: 'Remplir J avec les produits du template',
                    icon: <BiArrowToTop size={14} />,
                    onClick: fillJFromTemplate,
                },
                {
                    label: 'Copier J vers J+1',
                    icon: <BiArrowFromTop size={14} />,
                    onClick: fillJ1FromJ,
                },
            ],
        },
        {
            items: [
                {
                    label: 'Générer les quantités',
                    icon: <BiTargetLock size={14} />,
                    onClick: () => setIsQuantityGenerationPopupOpen(true),
                    requiredPermissions: [TotadminPermission.MicrostoreColumnsManage],
                },
                {
                    label: 'Générer les transferts',
                    icon: <FcNeutralTrading size={14} />,
                    onClick: () => setIsStockTransferPopupOpen(true),
                    requiredPermissions: [TotadminPermission.MicrostoreColumnsManage],
                },
            ],
        },
    ];

    return (
        <SectionContainer title="Emplacements" isInitiallyOpen>
            <>
                <EditorWrapper>
                    <MainArea $sidebarOpen={isSidebarOpen}>
                        <ActionsRow>
                            <DeliveryDateInfo>
                                {nextMSDeliveryDate ? (
                                    <>
                                        <DeliveryDateLabel>Prochaine livraison&nbsp;:</DeliveryDateLabel>
                                        <DeliveryDateValue>{nextMSDeliveryDate}</DeliveryDateValue>
                                    </>
                                ) : null}
                            </DeliveryDateInfo>
                            <ActionsAndSave
                                actionGroups={actionGroups}
                                actionsAlign="start"
                                onSave={submitUpdate}
                                saveDisabled={!isFormDirty}
                                saveLoading={updateLoading}
                                saveRequiredPermissions={[TotadminPermission.MicrostoreColumnsManage]}
                            />
                        </ActionsRow>
                        <ColumnLocationSelector
                            allSiteLocations={allSiteLocations}
                            earliestDLCDateToKeepForStockCalculation={
                                microstoreColumn.site.earliestDLCDateToKeepForStockCalculation || null
                            }
                            isSecured={microstoreColumn.isSiteSecured}
                            isToday={isToday}
                            microstoreProductsStockInfo={microstoreProductsStockInfo}
                            getMicrostoreProductsStockInfoLoading={stockInfoLoading}
                            nextSiteDeliveryDate={microstoreColumn.site.nextDeliveryDate || null}
                            removalDate={microstoreColumn.removalDate || null}
                            selectedLocationPosition={selectedLocationPosition}
                            selectedShelfIndex={selectedShelfIndex}
                            setIsToday={setIsToday}
                            setSelectedLocationPosition={setSelectedLocationPosition}
                            setSelectedShelfIndex={setSelectedShelfIndex}
                            shelves={columnFormValues.shelves}
                            updateLocationAt={updateLocationAt}
                            updateSelectedLocation={updateSelectedLocation}
                        />
                    </MainArea>
                    {isSidebarOpen && selectedLocation ? (
                        <SidebarArea>
                            <SidebarHeader>
                                <SidebarTitle>Emplacement</SidebarTitle>
                                <CloseButton
                                    type="button"
                                    onClick={() => {
                                        setSelectedShelfIndex(null);
                                        setSelectedLocationPosition(null);
                                    }}
                                >
                                    ✕
                                </CloseButton>
                            </SidebarHeader>
                            <LocationEditor
                                isToday={isToday}
                                microstoreProductsStockInfo={sidebarStockInfo}
                                selectedColumn={columnFormValues}
                                selectedLocation={selectedLocation}
                                updateSelectedLocation={updateSelectedLocation}
                                className="max-h-[600px]"
                            />
                            {(isToday && selectedLocation.productId !== null) ||
                            (!isToday && selectedLocation.replacementProductId !== null) ? (
                                <ExpiryDateEditor
                                    isToday={isToday}
                                    microstoreProductsStockInfo={sidebarStockInfo}
                                    selectedLocation={selectedLocation}
                                    updateSelectedLocation={updateSelectedLocation}
                                />
                            ) : null}
                        </SidebarArea>
                    ) : null}
                </EditorWrapper>
                {isStockTransferPopupOpen ? (
                    <StockTransferGenerationPopup
                        isOpen={isStockTransferPopupOpen}
                        setIsOpen={setIsStockTransferPopupOpen}
                        initialColumns={[
                            {
                                _id: microstoreColumn._id,
                                name: microstoreColumn.name,
                                siteName: microstoreColumn.siteName,
                            },
                        ]}
                        isLocked
                    />
                ) : null}
                {isQuantityGenerationPopupOpen ? (
                    <QuantityGenerationPopup
                        isOpen={isQuantityGenerationPopupOpen}
                        setIsOpen={setIsQuantityGenerationPopupOpen}
                        initialColumns={[
                            {
                                _id: microstoreColumn._id,
                                name: microstoreColumn.name,
                                siteName: microstoreColumn.siteName,
                            },
                        ]}
                        isLocked
                    />
                ) : null}
            </>
        </SectionContainer>
    );
};

const EmptyMessage = styled.p`
    color: ${({ theme }) => theme.infoTextColor};
    font-style: italic;
`;

const EditorWrapper = styled.div`
    display: flex;
    flex-direction: row;
    min-height: 400px;
`;

const MainArea = styled.div<{ $sidebarOpen: boolean }>`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const ActionsRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
`;

const DeliveryDateInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
`;

const DeliveryDateLabel = styled.span`
    color: ${({ theme }) => theme.infoTextColor};
`;

const DeliveryDateValue = styled.span`
    font-weight: 600;
`;

const SidebarArea = styled.div`
    width: 380px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    border-left: 1px solid ${({ theme }) => theme.lightBorderColor};
    margin-left: 12px;
    padding-left: 12px;
    overflow: hidden;
`;

const SidebarHeader = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
`;

const SidebarTitle = styled.span`
    font-size: 16px;
    font-weight: 600;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    font-size: 16px;
    padding: 4px 8px;
    color: ${({ theme }) => theme.textColor};

    &:hover {
        opacity: 0.7;
    }
`;
